import React, {memo, useCallback, useEffect, useState} from 'react';
import classNames from 'classnames';
import {SlimIcon} from '../icon/SlimIcon';
import {Colors} from '../../types/colorTypes';
import {HeaderType, TOCFontSizeEnum} from '../../types/block';

export const TOP_MENU_OFFSET = 120; // 70 Menu + 50 BlogMenu

export type TableOfContentsType = {
  dataTestId?: string;
  data: HeaderType[];
  tocTitle: string;
  className?: string;
  tocTitleClassName?: string;
  isExpandable: boolean;
  tocFontSize: TOCFontSizeEnum;
  tocColor: Colors;
};

export function getTableOfContents(data): HeaderType[] {
  return data.content.reduce((toc, block) => {
    if (block.__typename === 'DatoCmsHeader' && block.headerType[1] === '2') {
      toc.push({block});
    }
    return toc;
  }, []);
}

export const TableOfContents = memo((props: TableOfContentsType) => {
  const [isExpanded, setIsExpanded] = useState(props.isExpandable);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    if (props.isExpandable) {
      return;
    }
    const tocHeaders = Array.from(document.querySelectorAll('h2[data-toc-header]'));
    const viewPortBottomExit = window.innerHeight * 0.5;

    const intersectionCallback = headers => {
      headers.forEach(header => {
        if (header.isIntersecting) {
          setActiveSection(header.target.textContent);
        } else if (header.target.textContent == tocHeaders[0].textContent && header.boundingClientRect.top > TOP_MENU_OFFSET) {
          // We are above the first section from TOC
          setActiveSection(null);
        } else if (header.boundingClientRect.bottom > viewPortBottomExit && header.boundingClientRect.bottom < window.innerHeight) {
          // We are in between sections
          setActiveSection(tocHeaders[tocHeaders.indexOf(header.target) - 1].textContent); // Set previous section as active
        }
      });
    };

    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -50% 0px',
      threshold: 1
    };

    const observer = new IntersectionObserver(intersectionCallback, observerOptions);
    tocHeaders.forEach(header => observer.observe(header));

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleToggle = useCallback(() => {
    if (props.isExpandable === false) {
      return;
    }
    setIsExpanded(prevExpanded => !prevExpanded);
  }, [props.isExpandable]);

  const tocTitleClasses = classNames('text-sm text-grey font-bold leading-[1.5] font-body font-semibold my-1 sm:my-2', {
    'text-sm': props.tocFontSize === TOCFontSizeEnum.SMALL,
    'text-base': props.tocFontSize !== TOCFontSizeEnum.SMALL,
    'text-white': props.tocColor === Colors.PRIMARY
  });

  const tocContentClasses = classNames('text-grey visited:text-grey', {
    'text-white visited:text-white': props.tocColor === Colors.PRIMARY
  });

  const containerTocClass = classNames('max-xs:min-w-full', {
    'px-6': props.tocColor === Colors.BRIGHTGREY,
    'pt-3': props.tocColor === Colors.TRANSPARENT,
    'bg-blue-light px-2 py-3 border-l-2 border-t-0 border-r-0 border-b-0 border-solid border-primary': props.isExpandable
  });

  return (
    <div data-testid={props.dataTestId} className={classNames(containerTocClass, props.className)}>
      <button
        className='flex items-start w-full justify-between text-left bg-transparent border-0 cursor-pointer '
        onClick={handleToggle}
        aria-expanded={isExpanded}
        role='button'
        aria-label='Toggle table of contents'
      >
        <div className='flex items-start grow'>
          <span className='mr-3 mt-1 sm:mt-2'>
            <SlimIcon color={Colors.GREY} name='book' size='tiny' />
          </span>
          <span className={classNames(tocTitleClasses)} data-testid='toc-title'>
            {props.tocTitle}
          </span>
        </div>

        <svg
          width='14px'
          height='14px'
          data-testid='toc-toggle'
          viewBox='0 0 9 6'
          className={classNames('transition-transform duration-200 ease-in-out ml-4 mt-2 sm:mt-3  shrink-0', {
            'text-primary rotate-180': !isExpanded,
            'text-grey hover:text-primary': isExpanded,
            hidden: !props.isExpandable
          })}
        >
          <polyline
            transform='translate(4.500000, 3.000000) rotate(-90.000000) translate(-4.500000, -3.000000) translate(3.000000, -0.000000)'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            fill='none'
            points='3 6 0 3 3 0'
          ></polyline>
        </svg>
      </button>
      <div
        id='toc'
        data-testid='toc'
        className={classNames('mt-4 pb-3', {
          hidden: isExpanded,
          block: isExpanded,
          'lg:pb-0 lg:border-l-2 lg:border-t-0 lg:border-r-0 lg:border-b-0 lg:border-solid lg:border-primary': !props.isExpandable
        })}
      >
        {props.data.map(header => (
          <TocItem
            key={header.block.title}
            title={header.block.title}
            smallFont={props.tocFontSize === TOCFontSizeEnum.SMALL}
            classes={tocContentClasses}
            isExpandable={props.isExpandable}
            active={activeSection === header.block.title}
          />
        ))}
      </div>
    </div>
  );
});

const TocItem = memo(
  ({title, smallFont, classes, isExpandable, active}: {title: string; smallFont: boolean; classes: string; isExpandable: boolean; active: boolean}) => {
    const encodedTitle = encodeURI(title);

    return (
      <div
        data-testid='toc-item'
        className={classNames(classes, {
          'bg-grey-bright': active
        })}
      >
        <div className={classNames('py-3 lg:py-2 ml-4', {'text-sm': smallFont, 'md:ml-14': isExpandable})}>
          <a
            href={`#${encodedTitle}`}
            className={classNames('inline hover:text-primary', {
              'text-primary': active,
              'text-grey': !active
            })}
          >
            {title}
          </a>
        </div>
      </div>
    );
  }
);
